// src/store/slices/suggestionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import suggestionApi from 'api/suggestionApi';

export const fetchSuggestions = createAsyncThunk(
  'suggestion/fetchAll',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await suggestionApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load suggestions');
    }
  }
);

export const createSuggestion = createAsyncThunk(
  'suggestion/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await suggestionApi.create(data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to submit suggestion');
    }
  }
);

export const updateSuggestion = createAsyncThunk(
  'suggestion/update',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await suggestionApi.update(id, data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update suggestion');
    }
  }
);

export const deleteSuggestion = createAsyncThunk(
  'suggestion/delete',
  async (id, { rejectWithValue }) => {
    try {
      await suggestionApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete suggestion');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'suggestion/markAsRead',
  async (id, { rejectWithValue }) => {
    try {
      const response = await suggestionApi.markAsRead(id);
      return response;
    } catch (err) {
      return rejectWithValue('Failed to mark as read');
    }
  }
);

const suggestionSlice = createSlice({
  name: 'suggestion',
  initialState: {
    suggestions: [],
    pagination: { count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSuggestions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suggestions = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchSuggestions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createSuggestion.fulfilled, (state, action) => {
        state.suggestions.unshift(action.payload);
        state.pagination.count += 1;
      })
      .addCase(updateSuggestion.fulfilled, (state, action) => {
        const idx = state.suggestions.findIndex(s => s.id === action.payload.id);
        if (idx !== -1) state.suggestions[idx] = action.payload;
      })
      .addCase(deleteSuggestion.fulfilled, (state, action) => {
        state.suggestions = state.suggestions.filter(s => s.id !== action.payload);
        state.pagination.count -= 1;
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        const idx = state.suggestions.findIndex(s => s.id === action.payload.id);
        if (idx !== -1) state.suggestions[idx].is_read = true;
      });
  },
});

export const { clearError } = suggestionSlice.actions;
export default suggestionSlice.reducer;